import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, Pause, BadgeCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { StarRating } from "../components/StarRating";
import { motion } from "framer-motion";
import { useAudioPlayer } from "../contexts/AudioPlayerContext";
import { getPricing } from "../lib/pricing";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Home() {
  const [featuredBeats, setFeaturedBeats] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentTrack, isPlaying, playTrack } = useAudioPlayer();

  useEffect(() => {
    const fetchFeaturedBeats = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/beats?featured=true`);
        setFeaturedBeats(response.data);
      } catch (error) {
        console.error("Error fetching beats:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/comments?limit=6`);
        setReviews(response.data);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      }
    };

    fetchFeaturedBeats();
    fetchReviews();
  }, []);

  return (
    <div className="pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1528148343865-51218c4a13e6?w=1920&q=80"
            alt="Laptop and headphones"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/40" />
          <div className="absolute inset-0 hero-gradient" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-6 bg-primary/20 text-primary border-primary/30 px-4 py-1.5 text-sm">
                Premium Beat Marketplace
              </Badge>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-tight tracking-tight">
                Find your
                <span className="text-primary neon-text block">Truu Sound</span>
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-lg">
                Every beat has a story. It's up to you to tell the story. 
                High quality and professionally produced. Welcome to Truu Production Lane.
              </p>
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/store">
                  <Button 
                    size="lg" 
                    className="rounded-full px-8 py-6 bg-primary hover:bg-primary/90 neon-glow font-bold tracking-wide"
                    data-testid="browse-beats-btn"
                  >
                    Browse Beats
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/licensing">
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="rounded-full px-8 py-6 border-white/20 hover:bg-white/5 font-medium"
                    data-testid="view-licenses-btn"
                  >
                    View Licenses
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Beats Section */}
      <section className="py-20 px-6 lg:px-8" data-testid="featured-beats-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold">
                Featured Beats
              </h2>
              <p className="text-muted-foreground mt-2">
                Hand-picked tracks ready for your next project
              </p>
            </div>
            <Link to="/store">
              <Button variant="ghost" className="rounded-full gap-2" data-testid="view-all-btn">
                View All
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-card rounded-2xl p-4 animate-pulse">
                  <div className="w-full aspect-square bg-muted rounded-xl mb-4" />
                  <div className="h-6 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredBeats.map((beat, index) => (
                <motion.div
                  key={beat.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-card rounded-2xl border border-border p-4 hover:border-primary/50 transition-all beat-card"
                  data-testid={`featured-beat-${beat.id}`}
                >
                  <div className="relative mb-4">
                    <img
                      src={beat.cover_url?.startsWith("/api/") ? `${BACKEND_URL}${beat.cover_url}` : beat.cover_url}
                      alt={beat.title}
                      className="w-full aspect-square rounded-xl object-cover"
                    />
                    <Button
                      onClick={() => playTrack(beat, featuredBeats)}
                      size="icon"
                      className={`absolute bottom-3 right-3 w-12 h-12 rounded-full transition-all ${
                        currentTrack?.id === beat.id && isPlaying
                          ? "bg-primary opacity-100"
                          : "bg-black/60 opacity-0 group-hover:opacity-100"
                      }`}
                      data-testid={`featured-play-${beat.id}`}
                    >
                      {currentTrack?.id === beat.id && isPlaying ? (
                        <Pause className="w-5 h-5" />
                      ) : (
                        <Play className="w-5 h-5 ml-0.5" />
                      )}
                    </Button>
                  </div>
                  <h3 className="font-heading font-bold text-lg">{beat.title}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="secondary" className="font-mono text-xs">
                      {beat.bpm} BPM
                    </Badge>
                    <Badge variant="secondary" className="font-mono text-xs">
                      {beat.key}
                    </Badge>
                    {(beat.genres?.length ? beat.genres : [beat.genre]).slice(0, 2).map((g) => (
                      <Badge key={g} variant="outline" className="text-xs">
                        {g}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    {(() => {
                      const p = getPricing(beat);
                      if (p.isFree) return <Badge className="bg-green-500/20 text-green-400 font-bold">FREE</Badge>;
                      if (p.hasDiscount)
                        return (
                          <span className="flex items-baseline gap-2">
                            <span className="text-sm text-muted-foreground line-through">${p.original}</span>
                            <span className="font-heading font-bold text-xl text-primary">${p.final}</span>
                          </span>
                        );
                      return <span className="font-heading font-bold text-xl text-primary">${p.original}</span>;
                    })()}
                    <Link to="/store">
                      <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6 lg:px-8 bg-card/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 gap-8 max-w-xl mx-auto">
            {[
              { value: "24/7", label: "Instant Delivery" },
              { value: "100%", label: "Royalty Free" },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <p className="font-heading text-4xl lg:text-5xl font-black text-primary neon-text">
                  {stat.value}
                </p>
                <p className="text-muted-foreground mt-2">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Testimonials Section */}
      {reviews.length > 0 && (
        <section className="py-20 px-6 lg:px-8" data-testid="home-testimonials">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="font-heading text-3xl lg:text-4xl font-bold">What Artists Say</h2>
                <p className="text-muted-foreground mt-2">Real feedback from our community</p>
              </div>
              <Link to="/reviews">
                <Button variant="ghost" className="rounded-full gap-2" data-testid="view-all-reviews-btn">
                  All Reviews
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.slice(0, 3).map((c, i) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-card border border-border rounded-2xl p-6 flex flex-col"
                >
                  <StarRating value={c.rating} readOnly size={16} />
                  <p className="text-muted-foreground mt-3 flex-1 whitespace-pre-wrap">"{c.text}"</p>
                  <div className="flex items-center gap-2 mt-4">
                    <span className="font-bold">{c.name}</span>
                    {c.verified && (
                      <Badge className="bg-green-500/20 text-green-400 gap-1 text-xs">
                        <BadgeCheck className="w-3 h-3" /> Verified Buyer
                      </Badge>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
